import React, { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ChatBubbleLeftRightIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline'
import { handlePostRequest } from '../../utils/api-handler.util'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import { formatTime } from '../../utils/date.util'
import TextareaField from '../../components/common/TextareaField'
import USER_TYPE from '../../constants/userType'

const SupportTicketConversation = ({ ticket }) => {
  const [adminResponse, setAdminResponse] = useState('')

  const respondMutation = useMutation({
    mutationFn: () =>
      handlePostRequest(`/admin/support-tickets/${ticket?.id}/respond`, { message: adminResponse }),
    onSuccess: () => {
      QueryKeys.adminSupportTicketDetails.invalidate(Number(ticket.id))
      setAdminResponse('')
    },
  })

  if (!ticket) return null

  const renderMessage = (msg, idx) => {
    const isAdmin = msg.senderType === USER_TYPE.admin

    return (
      <div key={idx} className={`flex mb-4 ${isAdmin ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-xs lg:max-w-md xl:max-w-lg ${isAdmin ? 'order-2' : 'order-1'}`}>
          <div
            className={`px-4 py-3 rounded-2xl shadow-sm ${
              isAdmin
                ? 'bg-gradient-to-r from-secondary-600 to-button-orange-end text-white rounded-br-md'
                : 'bg-background border border-gray-200 text-foreground rounded-bl-md'
            }`}
          >
            <p className="text-sm leading-relaxed">{msg.message}</p>
          </div>
          <div
            className={`mt-1 flex justify-end gap-2 text-xs text-foreground-muted ${
              isAdmin ? 'text-right' : 'text-left'
            }`}
          >
            {!isAdmin && <div className="text-foreground-900 text-xs">{msg.senderName}</div>}
            <span> {formatTime(msg.sentAt)}</span>
            {isAdmin && <div className="text-foreground-900 text-xs ">{msg.senderName}</div>}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full mx-auto">
      <div className="flex-1 bg-background-secondary rounded-t-xl">
        <div className=" border-b border-gray-200 bg-background rounded-t-xl">
          <h3 className="text-xl font-semibold text-foreground mb-4">Support Conversation</h3>
        </div>

        <div className="flex-1 p-6 max-h-96 overflow-y-auto bg-background-secondary">
          {ticket.messages && ticket.messages.length > 0 ? (
            <div className="space-y-2">
              {ticket.messages.map((msg, idx) => renderMessage(msg, idx))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-32">
              <div className="text-center">
                <div className="w-16 h-16 bg-neutral-200-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <ChatBubbleLeftRightIcon className="w-8 h-8 text-foreground-muted" />
                </div>
                <p className="text-foreground-muted text-sm">No conversation yet.</p>
                <p className="text-foreground-muted text-xs mt-1">
                  Start the conversation by sending a message below.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-background border-t border-gray-200 rounded-b-xl ">
        <div className="flex items-start space-x-4">
          <div className="flex-1 flex gap-10">
            <TextareaField
              value={adminResponse}
              onChange={(e) => setAdminResponse(e.target.value)}
              placeholder="Type your response..."
              className="w-full flex-1"
              rows={2}
            />
            <div className="flex justify-between items-center mt-3 w-min">
              <button
                className="bg-gradient-to-r from-secondary-600 to-button-orange-end text-white px-6 py-2 rounded-lg font-medium hover:opacity-90 transition-opacity duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                onClick={async () => await respondMutation.mutateAsync()}
                disabled={
                  respondMutation.isLoading || !adminResponse.trim() || adminResponse.length > 500
                }
              >
                {respondMutation.isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon className="w-4 h-4" />
                    <span>Send</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SupportTicketConversation
